import React, { useEffect, useState } from 'react';
import { api } from '../../services/authService';

const MAX_FOLDER_DEPTH = 8;
/** Sentinel path value meaning folder_list is null/empty (Data Dictionary). */
export const NO_FOLDER_SENTINEL = '__no_folder__';
const foldersCache = new Map();
const foldersPromises = new Map();

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

function cacheKey(level, ancestors, visible) {
  const visibleKey = visible === true ? 'true' : visible === false ? 'false' : 'any';
  return `${level}\u0000${visibleKey}\u0000${ancestors.join('\u0001')}`;
}

function loadFolders(level, ancestors, visible = true) {
  const key = cacheKey(level, ancestors, visible);
  if (foldersCache.has(key)) return Promise.resolve(foldersCache.get(key));
  if (!foldersPromises.has(key)) {
    const params = { level, size: 500 };
    if (visible === true || visible === false) {
      params.visible = visible;
    }
    if (ancestors.length) {
      params.folder_path = ancestors.join(',');
    }
    const promise = api
      .get('/api/v2/segments/folders', { params })
      .then((res) => {
        const data = {
          hits: Array.isArray(res.data?.hits) ? res.data.hits : [],
          error: res.data?.error || '',
        };
        foldersCache.set(key, data);
        return data;
      })
      .catch((e) => {
        foldersPromises.delete(key);
        throw e;
      });
    foldersPromises.set(key, promise);
  }
  return foldersPromises.get(key);
}

/**
 * Cascading folder dropdowns: level 1, then "/" + level 2, etc.
 * `value` is an array of selected folder names; `onChange` receives the next array.
 * `trailing` is rendered after the path (with a "/") once there are no more subfolders.
 * When `includeNoFolderOption` is true, level 1 includes "No folder" (null/empty folder_list).
 * `visible` filters folder docs: true / false / null (any). Defaults to true for segment builder.
 */
const FolderPathSelect = ({
  value = [],
  onChange,
  trailing = null,
  includeNoFolderOption = false,
  visible = true,
}) => {
  const selected = Array.isArray(value) ? value : [];
  const isNoFolder =
    includeNoFolderOption && selected.length === 1 && selected[0] === NO_FOLDER_SENTINEL;
  const selectedKey = isNoFolder ? NO_FOLDER_SENTINEL : selected.join('\u0001');
  const [levelOptions, setLevelOptions] = useState({});
  const [levelErrors, setLevelErrors] = useState({});
  const [loadingLevels, setLoadingLevels] = useState({});
  const [nextLevelHasOptions, setNextLevelHasOptions] = useState(false);
  const [isLeaf, setIsLeaf] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (isNoFolder) {
      setNextLevelHasOptions(false);
      setIsLeaf(false);
      setLoadingLevels({});
      return () => {
        cancelled = true;
      };
    }

    const path = selectedKey ? selectedKey.split('\u0001') : [];
    const nextLevel = path.length + 1;
    const maxLoad = Math.min(Math.max(nextLevel, 1), MAX_FOLDER_DEPTH);
    // Hide the next dropdown / column until its fetch confirms leaf vs subfolders.
    setNextLevelHasOptions(false);
    setIsLeaf(path.length >= MAX_FOLDER_DEPTH);

    const loadLevel = async (level) => {
      const ancestors = path.slice(0, level - 1);
      setLoadingLevels((prev) => ({ ...prev, [level]: true }));
      try {
        const data = await loadFolders(level, ancestors, visible);
        if (cancelled) return;
        setLevelOptions((prev) => ({ ...prev, [level]: data.hits }));
        setLevelErrors((prev) => ({ ...prev, [level]: data.error || '' }));
        if (level === nextLevel && path.length > 0 && path.length < MAX_FOLDER_DEPTH) {
          setNextLevelHasOptions(data.hits.length > 0);
          setIsLeaf(data.hits.length === 0);
        }
      } catch (e) {
        if (cancelled) return;
        setLevelOptions((prev) => ({ ...prev, [level]: [] }));
        setLevelErrors((prev) => ({
          ...prev,
          [level]: formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load folders',
        }));
        if (level === nextLevel && path.length > 0) {
          setNextLevelHasOptions(false);
          setIsLeaf(true);
        }
      } finally {
        if (!cancelled) {
          setLoadingLevels((prev) => ({ ...prev, [level]: false }));
        }
      }
    };

    for (let level = 1; level <= maxLoad; level += 1) {
      loadLevel(level);
    }

    return () => {
      cancelled = true;
    };
  }, [selectedKey, isNoFolder, visible]);

  let levelsToShow = 1;
  if (!isNoFolder && selected.length > 0) {
    levelsToShow = selected.length;
    if (nextLevelHasOptions && selected.length < MAX_FOLDER_DEPTH) {
      levelsToShow += 1;
    }
  }

  const handleSelect = (levelIndex, name) => {
    if (!name) {
      onChange(selected.slice(0, levelIndex));
      return;
    }
    if (includeNoFolderOption && levelIndex === 0 && name === NO_FOLDER_SENTINEL) {
      onChange([NO_FOLDER_SENTINEL]);
      return;
    }
    onChange([...selected.slice(0, levelIndex), name]);
  };

  const firstError = Object.keys(levelErrors)
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => levelErrors[k])
    .find(Boolean);

  return (
    <div className="criteria-folder-path">
      <div className="criteria-folder-cascade" role="group" aria-label="Folder path">
        {Array.from({ length: levelsToShow }, (_, i) => {
          const level = i + 1;
          const options = levelOptions[level] || [];
          const loading = Boolean(loadingLevels[level]);
          const current = selected[i] || '';
          return (
            <React.Fragment key={level}>
              {i > 0 && <span className="criteria-folder-sep" aria-hidden>/</span>}
              <select
                className="criteria-folder-select"
                value={current}
                disabled={loading && !isNoFolder}
                aria-label={`Folder level ${level}`}
                onChange={(e) => handleSelect(i, e.target.value)}
              >
                <option value="">{loading && !isNoFolder ? 'Loading…' : 'Select folder…'}</option>
                {includeNoFolderOption && level === 1 && (
                  <option value={NO_FOLDER_SENTINEL}>No folder</option>
                )}
                {current &&
                  current !== NO_FOLDER_SENTINEL &&
                  !options.some((h) => h.name === current) && (
                    <option value={current}>{current}</option>
                  )}
                {options.map((h) => (
                  <option key={h._id || h.name} value={h.name}>
                    {h.name}
                  </option>
                ))}
              </select>
            </React.Fragment>
          );
        })}
        {!isNoFolder && isLeaf && trailing != null && (
          <>
            <span className="criteria-folder-sep" aria-hidden>/</span>
            {trailing}
          </>
        )}
      </div>
      {firstError && (
        <p className="criteria-empty" role="alert">
          {firstError}
        </p>
      )}
    </div>
  );
};

export default FolderPathSelect;
